// @flow

import _ from 'lodash';
import { $default, $set, update } from 'qim';

import FieldUtils from 'app/beta-editor/utils/FieldUtils';
import ServiceUtils from 'app/service/ServiceUtils';
import NodeLabels, {
  getActionLabelFromService,
} from 'app/beta-editor/NodeLabels';
import { get } from 'app/common/CommonUtils';
import { getAncestors } from 'app/beta-editor/utils/NodeTrees';
import { nodeTypes } from 'app/service/constants';
import {
  DEFAULT_BETA_ACTION_LABEL,
  DEFAULT_BETA_TRIGGER_LABEL,
  SIMPLIFIED_HEADER_TXT_ACTION,
  SIMPLIFIED_HEADER_TXT_TRIGGER,
  ZT_API_NAMESPACE,
  ZT_LIVE_DATA_SERVICES,
} from 'app/beta-editor/constants';
import {
  getErrorForNode,
  getNewestCrumbForId,
  wasNodeRan,
} from 'app/beta-editor/utils/TaskStateUtils';
import {
  getPathName,
  isPathNode,
  isPathsGroupNode,
} from 'app/beta-editor/utils/PathUtils';
import {
  getStepIndex,
  hasNeeds,
  isFlowTemplate,
  isSearchStep,
  isTriggerStep,
} from 'app/beta-editor/utils/FlowUtils';
import { isFilter, isRead } from 'app/service/utils.js';

import type {
  DirtyTaskState,
  Flow,
  FlowNode,
  FlowOwner,
  FlowSteps,
  FlowTaskState,
  NodeLoading,
  NodeMap,
  NodesSampleData,
  SkinnyNode,
  StaleSamples,
  TestStatus,
} from 'app/beta-editor/flowState';
import type {
  EditorAccount,
  Field,
  Give,
  StepType,
  UpdateField,
} from 'app/beta-editor/types';
import type { ID } from 'app/common/types';
import type { Service, ServicesMap } from 'app/service/types';
import type { Task } from 'app/history/types';

const EMPTY_ZAP_NAME = 'Name your zap';

const OUT_OF_SYNC = 'out-of-sync';

const _recurserToStrings = <T>(obj: T, func: T => void) => {
  // finds nested strings and calls back for each one
  if (typeof obj === 'string') {
    func(obj);
  } else if (
    Array.isArray(obj) ||
    (typeof obj === 'object' && typeof obj !== 'function')
  ) {
    _.each(obj, value => {
      _recurserToStrings(value, func);
    });
  }
};

// return map of {nodeid: [nodeid, ...]} according to {{\D__\W}}
// annoying to do three passes - but it is super clear this way
const dependencies = (nodesMap: NodeMap) => {
  const dependencyMap = {};

  let rootId = null;
  _.each(nodesMap, (node, nodeId) => {
    if (!(node.parent || node.parent_id)) {
      rootId = Number(nodeId);
    }
  });

  _.each(nodesMap, (node, nodeId) => {
    if (node.parent || node.parent_id) {
      dependencyMap[nodeId] = [rootId];
    } else {
      dependencyMap[nodeId] = [];
    }
  });

  _.each(nodesMap, (node, nodeId) => {
    const matches = [];

    // TODO: if we found {{some_key}} do we include all parents
    // as dependencies since we don't know?
    _recurserToStrings(node.params, function(str) {
      const re = /{{(\d+__)?([^}]+)}}/g;
      let match = re.exec(str);
      while (match !== null) {
        matches.push(match);
        match = re.exec(str);
      }
    });

    _.each(matches, function(match) {
      // let [complete, dependentId, name] = match;
      let dependentId = match[1];
      if (dependentId) {
        dependentId = Number(dependentId.replace(/\D/g, ''));
        if (
          nodesMap[_.toString(dependentId)] &&
          !_.includes(dependencyMap[nodeId], dependentId)
        ) {
          dependencyMap[nodeId].push(dependentId);
        }
      }
    });
  });

  return dependencyMap;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const isNodeTemplate = (node: FlowNode) => _.get(node, ZT_API_NAMESPACE, false);

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/**
 * TODO(neilff):
 * We should refactor this function and other functions similar to it. We
 * should instead expect this function to receive a single service and all
 * of its details rather than the list of all services and their details.
 */
export const getServiceAssociatedWithNode = (
  node: ?FlowNode,
  services: ServicesMap
) => {
  if (!node || !services || !node.selected_api) {
    return null;
  }

  return _.get(services, node.selected_api, null);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
export const serviceNeedsAuth = (node: FlowNode, services: ServicesMap) => {
  const svc = getServiceAssociatedWithNode(node, services);
  return svc && ServiceUtils.needsAuth(svc);
};

/**
 * Return true if the node has an auth, or if the service
 * associated with the node does not require authentication.
 */
export const nodeHasAuthIfNeeded = (node: FlowNode, services: ServicesMap) =>
  !serviceNeedsAuth(node, services) || Boolean(node.authentication_id);

const firstOutputOfSample = (value: Array<FlowTaskState> | {}) =>
  Array.isArray(value) ? _.head(value) || {} : value;

const sample = (node: ?FlowNode, taskState: FlowTaskState | Task) => {
  if (node && taskState) {
    const input = taskState.input || {};
    return {
      input: input[node.id],
      output: firstOutputOfSample((taskState.output || {})[node.id]),
      status: (taskState.statuses || {})[node.id],
      error: getErrorForNode(taskState, node.id),
    };
  } else {
    return {
      input: null,
      output: null,
      status: null,
      error: null,
    };
  }
};

const hasSampleData = (node: FlowNode, taskState: FlowTaskState) => {
  const nodeSample = sample(node, taskState);
  const isTrigger = isTriggerStep(node);
  const taskOutput = _.get(taskState, ['output', node.id]);
  const hasSamples = isTrigger
    ? _.isObject(taskOutput) && !_.isEmpty(taskOutput)
    : _.isObject(taskOutput);
  return nodeSample.status === 'success' && hasSamples;
};

const hasNonSkipSearchResult = (node: FlowNode, taskState: FlowTaskState) => {
  const isSearch = isSearchStep(node);
  const taskOutput = _.get(taskState, ['output', node.id], {});
  // taskOutput might be null
  return isSearch && taskOutput && !taskOutput._zap_data_was_skipped;
};

const hasFilterData = (node: FlowNode, taskState: FlowTaskState) => {
  const crumb = getNewestCrumbForId(node.id, taskState);
  return node.type_of === 'filter' && !!_.get(crumb, 'passed_ids');
};

const metaDataPrefix = '_zap_data';

const isSampleDataOnlyMetadata = (data?: {} = {}) =>
  Object.keys(data).every(k => k.indexOf(metaDataPrefix) === 0);

const isSampleDataEffectivelyEmpty = (data: ?{}) =>
  _.isEmpty(data) || (data && isSampleDataOnlyMetadata(data));

// Canonical place to find out the status of a node's sample. There are two "layers" at play.
// First is the transient status of the frontend communicating with the backend:
//   unknown - we have not loaded the data from the server yet
//   waiting - we're trying to get data right now
//   out-of-sync - we have data, but it might be out of sync with the server
// Once we have successfully communicated with the backend, the second layer is the status of the sample
//   untried - node has not been run yet
//   nodata - successful API call to 3rd party, but there wasn't anything found (i.e. actions that don't return. Empty poll)
//   error - we got an error running the node
//   data - successfully ran the node and we have data
//   stale - we have data, but the server says it is stale, so user needs to OK another live run
const sampleStatus = (
  node: FlowNode,
  {
    taskState,
    dirtyTaskState,
    nodeLoading,
    staleSamples,
  }: {
    taskState: FlowTaskState,
    dirtyTaskState: DirtyTaskState,
    nodeLoading: NodeLoading,
    staleSamples: StaleSamples,
  }
): ?TestStatus => {
  const nodeSample = sample(node, taskState);

  if (!taskState) {
    return 'unknown';
  } else if (nodeLoading[node.id]) {
    return 'waiting';
  } else if (dirtyTaskState[node.id]) {
    return 'out-of-sync';
  } else if (staleSamples[node.id]) {
    return 'stale';
  } else if (nodeSample.status === 'error' || nodeSample.status === 'held') {
    return 'error';
  } else if (nodeSample.status === 'halted') {
    // backend returns halted for empty search
    if (node.type_of === 'search') {
      return 'nodata';
    }
    return 'error';
  } else if (!wasNodeRan(taskState, node.id)) {
    return 'untried';
  } else if (
    nodeSample.status === 'success' &&
    isSampleDataEffectivelyEmpty(nodeSample.output)
  ) {
    return 'nodata';
  } else if (hasSampleData(node, taskState) || hasFilterData(node, taskState)) {
    return 'data';
  } else {
    return null;
  }
};

// Path and paths group nodes never have gives
const isGivableNode = (node: FlowNode) =>
  !isPathsGroupNode(node) && !isPathNode(node);

export const doAncestorsHaveFreshSampleData = (node: FlowNode, flow: Flow) => {
  // If ZT we just assume that sample data is valid.
  if (isNodeTemplate(node)) return true;

  const nodeAncestors = getAncestors(flow.nodes, node.id).filter(isGivableNode);
  if (nodeAncestors.length === 0) {
    return true;
  }

  return _.every(
    _.map(nodeAncestors, ancestor => {
      const status = sampleStatus(ancestor, {
        taskState: flow.taskState,
        dirtyTaskState: flow.dirtyTaskState,
        nodeLoading: flow.nodeLoading,
        staleSamples: flow.staleSamples,
      });
      // TODO: When all writes are flow-compatible, remove this special nodata case
      return (
        status === 'data' ||
        (ancestor.type_of === 'write' && status === 'nodata')
      );
    })
  );
};

const nodeAction = (node: ?FlowNode, services: ServicesMap) => {
  if (!node) return undefined;
  const _service = getServiceAssociatedWithNode(node, services) || {};
  return _.find(_service[node.type_of], { key: node.action });
};

const constructHookURL = function(node, action) {
  let value = '';
  _.each(action.needs || [], need => {
    // Is this ever insufficient? I don't love this section, but I'm not aware
    // of what other edge cases for which we need to account.
    if (_.isArray(need.input_format)) {
      if (!value) {
        value = _.map(need.input_format, need_format_chunk => {
          // Turn any nulls or undefineds into empty strings
          need_format_chunk = need_format_chunk || '';

          // If we're dealing with the value from an input, snag its value from
          // the node's params
          if (need_format_chunk === '{{input}}') {
            need_format_chunk = node.params[need.key];
          }
          return need_format_chunk;
        }).join('');
      } else {
        console.warn(
          'Existing JavaScript architecture does not support a field with >1 `need` defining `input_format`. Ignoring need with key `' +
            need.key +
            '`.'
        );
      }
    }
  });
  return value;
};

const hasHookURL = (node: FlowNode, services: ServicesMap) => {
  const action = nodeAction(node, services);
  if (!action) {
    return false;
  }
  return Boolean(action.hook_url || action.hook_legacy_url); // node.hasHookURL
};

const hookDirections = (node: FlowNode, services: ServicesMap) => {
  const action = nodeAction(node, services);
  if (!action || !action.hook_directions_html) {
    return '';
  }
  return action.hook_directions_html; // node.hookDirections
};

// Pass owner data in `options`, don't pass `flow`
const staticHookIdentifier = (
  {
    node,
    owner,
    services,
  }: {
    node: FlowNode,
    owner: FlowOwner,
    services: ServicesMap,
  },
  options?: { isSilent: boolean } = { isSilent: false }
) => {
  const action = nodeAction(node, services);

  // Bounce if it's not a hook
  if (!action || !action.hook) {
    return null;
  }

  // Hooks that lack an explicit `hook_url` value need one deduced.
  let hookUrl = action.hook_url;
  if (!hookUrl) {
    hookUrl = constructHookURL(node, action);
  }

  const node_params_identifier = get(node, ['params', 'identifier']);
  const context = {
    node_params_identifier,
    email: _.get(owner, ['email']), // mainly for zillow
    cid: _.get(owner, ['id']), // mainly for paypal
    ccode: _.get(owner, ['code']), // mainly for paypal
    sid: node.id, // get(object, ['id']), // ancient legacy
    scode: node.code, // get(object, ['get', ['code']]), // ancient legacy
    old_code: node.legacy && node.legacy.code ? node.legacy.code : null, // legacy, pre flow
    code: node.code, // current
  };
  _.each(context, (value, key) => {
    hookUrl = hookUrl.replace('{{' + key + '}}', value);
  });
  if (options.isSilent) {
    hookUrl = hookUrl + 'silent/';
  }
  return hookUrl;
};

const isStaticHook = (node: FlowNode, services: ServicesMap) => {
  const action = nodeAction(node, services) || {};
  return Boolean(action.hook);
};

const isRestHook = (node: FlowNode, services: ServicesMap) => {
  const action = nodeAction(node, services) || {};
  return Boolean(action.hook_subscription || action.hook_notification);
};

const isInstant = (node: FlowNode, services: ServicesMap) => {
  return Boolean(isRestHook(node, services) || isStaticHook(node, services));
};

const isHookPollable = (node: FlowNode, services: ServicesMap) => {
  const action = nodeAction(node, services) || {};
  return Boolean(action.hook_pollable);
};

const isHookToPoll = (node: FlowNode, services: ServicesMap) => {
  const action = nodeAction(node, services) || {};
  return Boolean(action.hook_to_poll);
};

export const canHookBeSilent = (node: FlowNode, service: Service) =>
  node.type_of === 'read' && service.key === 'WebHookAPI';

export const canHookSetupBeSkipped = (flow: Flow, node: FlowNode) =>
  node.type_of === 'read' &&
  node.selected_api === 'WebHookAPI' &&
  hasNeeds({ flow, node });

export const isHookSilent = (node: FlowNode) =>
  _.get(node, ['meta', 'webhook', 'is_silent']);
/*
   This does a deep clone of meta - applying the defaults we expect.

   A shallow _.clone is not enough to ensure we aren't touching objects
   back in the flux store!
 */
const normalizedMeta = (node: FlowNode) =>
  update(
    [
      ['parammap', $default({})],
      ['selectedGives', $default({})],
      [
        'validation',
        [
          'params',
          ['invalid_mappings', $default({})],
          ['is_opened', $default(false)],
          ['is_valid', $default(false)],
          ['is_edited', $default(false)],
        ],
        ['webhook', 'is_opened', $default(false)],
      ],
    ],
    node.meta || {}
  );

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// Takes a list of nodes and a set of invalid mappings and merges the errors into meta.
// This will also clear out pre-existing warnings in meta that are now valid mappings for the given parent node.
const mergeNodeMetaInvalidMappings = function(
  descendantNodes: Array<FlowNode>,
  invalidMappings: {},
  parentNodeId: string
) {
  const parentGiveRegex = new RegExp('{{' + parentNodeId);

  const updatedNodes = [];

  _.each(descendantNodes, descendant => {
    const metaForNode = normalizedMeta(descendant);
    const fieldKeys = _.union(
      _.keys(metaForNode.validation.params.invalid_mappings),
      _.keys(invalidMappings[descendant.id])
    );

    const updatedInvalidMappings = {};

    _.each(fieldKeys, fieldKey => {
      // Strip any existing errors that are for this parent node, leaving errors from other nodes
      let mappingsForField = _.filter(
        metaForNode.validation.params.invalid_mappings[fieldKey],
        mapping => {
          return !parentGiveRegex.test(mapping);
        }
      );

      // Now add in the most current errors for this parent node, if there still are any
      if (
        invalidMappings[descendant.id] &&
        invalidMappings[descendant.id][fieldKey]
      ) {
        mappingsForField = mappingsForField.concat(
          invalidMappings[descendant.id][fieldKey]
        );
      }

      if (!_.isEmpty(mappingsForField)) {
        updatedInvalidMappings[fieldKey] = mappingsForField;
      }
    });

    updatedNodes.push(
      update(
        [
          'meta',
          'validation',
          'params',
          'invalid_mappings',
          $set(updatedInvalidMappings),
        ],
        descendant
      )
    );
  });

  return updatedNodes;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/**
   Compare the desiredAttrs subset only.
 */
const isEqualNodeAttrs = (currentAttrs: {}, desiredAttrs: {}) => {
  const comparisonAttrs = _.pick(currentAttrs, _.keys(desiredAttrs));
  return _.isEqual(comparisonAttrs, desiredAttrs);
};

/**
   If the desiredAttrs differ from the currentAttrs - mixin the
   overrideAttrs.

   Useful for when you need to null out some dependent attrs *only*
   when particular attrs are changing - therefore invalidating them.

   For example - a changing selected_api invalidates authentication_id,
   action, params, and meta.
 */
const mergeNodeAttrs = (
  currentAttrs: FlowNode,
  desiredAttrs: {},
  overrideAttrs: {}
) => {
  let cleanedAttrs;

  if (isEqualNodeAttrs(currentAttrs, desiredAttrs)) {
    cleanedAttrs = desiredAttrs;
  } else {
    // TODO: react-addons-update here?
    cleanedAttrs = _.extend(desiredAttrs, overrideAttrs);
  }
  return cleanedAttrs;
};

const _recurseFindStrings = (obj: any, func: (string: string) => void) => {
  if (_.isString(obj)) {
    func(obj);
  } else if (_.isObject(obj) && !_.isFunction(obj)) {
    // object or array - not function
    _.each(obj, (value, key) => {
      _recurseFindStrings(key, func);
      _recurseFindStrings(value, func);
    });
  }
};

/**
   Given a node, return curlies in params:
   [{raw: '{{1__value}}', id: 1, key: 'value'}, ...]
 */
const nodeParamsCurlies = (node: FlowNode) => {
  const params = node.params || {};
  const matches = [];
  _recurseFindStrings(params, function(str) {
    const re = /\{\{(\d+__)?([^{}]+)\}\}/g;
    let match = re.exec(str);
    while (match !== null) {
      matches.push(match);
      match = re.exec(str);
    }
  });
  return _.map(matches, match => {
    return {
      raw: match[0], // '{{1__value}}'
      id: match[1] ? Number(match[1].slice(0, -2)) : undefined, // 1 or undefined
      key: match[2], // 'value'
    };
  });
};

/**
   Find all {{1__value}} usages and pair it with a label from the gives
   if we can find it. Returns object of {'1__value': 'Some Lable'} suitable
   for node.meta.selectedGives.
 */
const inUseSelectedGivesMap = (node: FlowNode, gives: Array<Give>) => {
  const givesLabelMap = FieldUtils.givesLabelMap(gives);
  const meta = node.meta || {};
  const lastGivesLabelMap = meta.selectedGives || {};

  const grabGiveLabel = ({ raw }) => {
    raw = raw.replace('{{', '').replace('}}', '');
    const label = givesLabelMap[raw] || lastGivesLabelMap[raw];
    if (label) {
      return [raw, label];
    }
    return null;
  };

  return _.fromPairs(_.compact(_.map(nodeParamsCurlies(node), grabGiveLabel)));
};

/**
   Given a node, return replaceChoices backfilled from persisted labels
   or at least slightly prettier keys. :-/
 */
const tempReplaceChoicesFromNode = (field: Field) => {
  const node = _.get(field, ['meta', 'node'], {});
  const meta = node.meta || {};
  const selectedGives = meta.selectedGives || {}; // {key: value, ...}

  return _.map(selectedGives, (label: string, keyRaw: string) => {
    const key = keyRaw.replace('{{', '').replace('}}', '');

    return {
      value: key,
      label,
      sample: undefined, // would be cool if we persisted a sample
    };
  });
};

export const hasInvalidMappings = (node: FlowNode) => {
  const meta = node.meta;
  return !_.isEmpty(
    _.values(_.get(meta, ['validation', 'params', 'invalid_mappings']))
  );
};

// Simple rule for now. Write and search nodes invalidate.
const shouldInvalidatedNeeds = (node: FlowNode) => {
  return (
    node.type_of === 'read' ||
    node.type_of === 'write' ||
    node.type_of === 'search' ||
    node.type_of === 'search_or_write'
  );
};

const hasShownInfo = (node: FlowNode) =>
  Boolean(_.get(node, ['meta', 'validation', 'info', 'is_opened']));

const hasShownAction = (node: FlowNode) => {
  return !!_.get(node, ['meta', 'validation', 'action', 'is_opened']);
};

const isValidApp = (node: FlowNode) => {
  return !!node.selected_api;
};

const isValidAction = (node: FlowNode) => {
  return !!node.action;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const isValidAuth = (node: FlowNode, services: ServicesMap) => {
  const doesntNeedAuth =
    isNodeTemplate(node) || !serviceNeedsAuth(node, services);
  if (doesntNeedAuth) {
    return true;
  }
  return Boolean(node.authentication_id);
};

export const isValidFields = (node: FlowNode, flow: Flow) => {
  const areParamsValid = _.get(
    node.meta,
    ['validation', 'params', 'is_valid'],
    false
  );
  const areParamsOpened = _.get(
    node.meta,
    ['validation', 'params', 'is_opened'],
    false
  );

  const needs = (flow.needs || {})[node.id];
  const mappingsInvalid = hasInvalidMappings(node);
  const isZTUsingAppSampleData =
    isFlowTemplate(flow.nodes) && !node.authentication_id;
  return (
    isZTUsingAppSampleData ||
    (!mappingsInvalid &&
      ((needs && needs.length === 0) || (areParamsValid && areParamsOpened)))
  );
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const requiresLiveData = (node?: FlowNode) => {
  return Boolean(node && _.includes(ZT_LIVE_DATA_SERVICES, node.selected_api));
};

const isTemplateNodeUsingSampleData = (node?: FlowNode) =>
  Boolean(node && isNodeTemplate(node) && !requiresLiveData(node));

const isValidSampleStatus = (node: FlowNode, status: ?TestStatus) => {
  return (
    // Assume all sample data in ZTs are valid
    isTemplateNodeUsingSampleData(node) ||
    status === 'data' ||
    // TODO: When all writes are flow-compatible, remove this special nodata case
    (status === 'nodata' && node.type_of === 'write')
  );
};

export const isValidSample = (
  node: FlowNode,
  { taskState, dirtyTaskState, nodeLoading, staleSamples }: NodesSampleData
) =>
  isValidSampleStatus(
    node,
    sampleStatus(node, {
      taskState,
      dirtyTaskState,
      nodeLoading,
      staleSamples,
    })
  );

// Really should not have to pass flow here, but the store is organizing data incorrectly.
// Needs to be inverted so we can grab all the metadata for a node.
const isValid = (node: FlowNode, flow: Flow, services: ServicesMap) => {
  // paths group nodes never have any setup, are always valid
  if (isPathsGroupNode(node)) {
    return true;
  }

  return (
    isValidAuth(node, services) &&
    isValidApp(node) &&
    isValidAction(node) &&
    isValidFields(node, flow) &&
    isValidSample(node, flow)
  );
};

// same as the above, but zaps can be activated with invalid mapping warnings,
// so don't include that criteria when trying to turn on a zap. The above is
// used for a lot of situations where invalid fields _do_ matter, however
// that's not the case for turning on a zap, as is demonstrated by
// the dashboard page which raises no errors during activation if the zap has
// invalid fields.
export const isActivatableNode = (
  node: FlowNode,
  nodesSampleData: NodesSampleData,
  services: ServicesMap
) => {
  // paths group nodes never have any setup, are always valid
  if (isPathsGroupNode(node)) {
    return true;
  }

  return (
    isValidAuth(node, services) &&
    isValidApp(node) &&
    isValidAction(node) &&
    isValidSample(node, nodesSampleData)
  );
};

export const isAutomappedField = (node: SkinnyNode, key: string) =>
  _.get(
    node,
    ['meta', 'validation', 'params', 'automapped_fields', key],
    false
  );

const pollingInterval = (flow: Flow, account: EditorAccount): number => {
  const rootNode = flow.nodes[flow.id];
  return (
    rootNode.polling_interval_override ||
    _.get(account, ['account', 'limits', 'polling_interval'], 0)
  );
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const paramKey = (node: SkinnyNode, fieldKey: string) =>
  `${node.id}__${fieldKey}`;

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const updateNodeParams = (node: SkinnyNode, updateField: UpdateField) => {
  const key = paramKey(node, updateField.newNodeFieldKey);
  const mustacheKey = `{{${key}}}`;

  if (updateField.parentKey) {
    const params = updateField.params[updateField.parentKey] || [];
    const parentParams = update(
      [updateField.fieldKey, $set(mustacheKey)],
      params
    );
    return { ...updateField.params, [updateField.parentKey]: parentParams };
  } else {
    return { ...updateField.params, [updateField.fieldKey]: mustacheKey };
  }
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const updateSearchfillsMeta = (node: SkinnyNode, updateField: UpdateField) => {
  const key = paramKey(node, updateField.newNodeFieldKey);
  return update(
    ['searchfills', key, $set(updateField.fieldLabel)],
    updateField.meta || {}
  );
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const newNodeMeta = (node: SkinnyNode, updateField: UpdateField) => {
  const key = paramKey(node, updateField.newNodeFieldKey);

  return {
    selectedGives: {
      [key]: updateField.fieldLabel,
    },
    parammap: {
      [key]: updateField.fieldLabel,
    },
  };
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/*
   Returns true if node has a selected_api,
   action, and either has an auth or is using
   a service that does not require auth.

   This check need to match up with the backend
   is_configured check - flow.models.is_configured.
 */
const isConfigured = (node: FlowNode, services: ServicesMap) =>
  Boolean(node) &&
  Boolean(node.selected_api && node.action && isValidAuth(node, services));

const isSampleOutOfSync = (node: FlowNode, flow: Flow) => {
  const status = sampleStatus(node, flow);
  return status === OUT_OF_SYNC;
};

const hasSelectedApi = (selectedApi: string, node: SkinnyNode) => {
  return node.selected_api === selectedApi;
};

export const hasFilterApi = _.partial(hasSelectedApi, 'FilterAPI');

const hasCodeApi = _.partial(hasSelectedApi, 'CodeAPI');

export const isFilterNode = (node: SkinnyNode) =>
  !isPathNode(node) && isFilter(node.type_of);

const isFilterWithEmptyCriteria = (node: ?FlowNode) => {
  if (!node) {
    return false;
  } else {
    const isFilterStep = isFilterNode(node);
    const isFilterCriteriaValid = !FieldUtils.filterCannotContinue(node);
    return isFilterStep && !isFilterCriteriaValid;
  }
};

// 'paths_group' is a pseudo node types for now. Later
// it will be it's own type on the backend.
export const getNodeType = (node: SkinnyNode) => {
  if (isPathsGroupNode(node)) {
    return nodeTypes.PATHS_GROUP;
  }
  return node.type_of;
};

export const isLastStepInZap = (
  flowId: ID,
  nodes: NodeMap,
  node: SkinnyNode,
  steps: FlowSteps
) => getStepIndex(flowId, nodes, node.id) === steps.length - 1;

export const getStepTitle = (node: SkinnyNode) =>
  isPathNode(node) ? getPathName(node) : _.get(node, ['meta', 'stepTitle']);

const hasUserDefinedStepTitle = (node: SkinnyNode) =>
  !_.isEmpty(getStepTitle(node));

export const getRetainedNodeMeta = (node: SkinnyNode) =>
  hasUserDefinedStepTitle(node) ? { stepTitle: getStepTitle(node) } : {};

export const getStepLabel = (step: StepType) => _.get(step, 'label', '');

// DEPRECATED: Use getDefaultStepTitle, getStepCardLabel instead.
// This function is only used for the legacy ZT Creator finalize
// step. Once we get rid of that we can get rid of this function.
export const getStepTitleWithServiceName = ({
  node,
  services,
}: {
  node: FlowNode,
  services: ServicesMap,
}) => {
  const service = getServiceAssociatedWithNode(node, services);
  const serviceName = service ? ServiceUtils.getServiceName(service) : '';
  const actionLabel = NodeLabels.actionLabel(node, services);
  const title =
    getStepTitle(node) ||
    actionLabel ||
    (isTriggerStep(node) && DEFAULT_BETA_TRIGGER_LABEL) ||
    DEFAULT_BETA_ACTION_LABEL;

  const isBuiltInApp = service ? service.builtIn : false;
  if (!serviceName || isBuiltInApp || isPathNode(node)) {
    return title;
  }

  const isNotDefaultLabel =
    title !== DEFAULT_BETA_TRIGGER_LABEL && title !== DEFAULT_BETA_ACTION_LABEL;

  return isNotDefaultLabel ? `${title} in ${serviceName}` : serviceName;
};

/**
 * Find the service associated with a node. First try finding the
 * service from the list of `fullServices`, which include all the actions
 * and such. If not found, try finding the service from the list of all 'skinny 'services.
 * We can still glean useful information from the 'skinny' service like the service name.
 */
export const findService = ({
  node,
  fullServices,
  allServices,
}: {
  node: FlowNode,
  fullServices: ServicesMap,
  allServices: Array<Service>,
}) => {
  if (!node) {
    return undefined;
  }

  return (
    getServiceAssociatedWithNode(node, fullServices) ||
    allServices.find(svc => svc.key === node.selected_api)
  );
};

const hasBuiltInApp = service => get(service, ['builtIn'], false);

const isZapierApp = ({ node, service }) =>
  hasBuiltInApp(service) || isPathNode(node);

const getServiceName = service =>
  service ? ServiceUtils.getServiceName(service) : '';

export const getDefaultStepTitle = ({
  node,
  service,
}: {
  node: FlowNode,
  service?: Service,
}) =>
  getActionLabelFromService(node, service) ||
  getServiceName(service) ||
  (isTriggerStep(node) && SIMPLIFIED_HEADER_TXT_TRIGGER) ||
  SIMPLIFIED_HEADER_TXT_ACTION;

export const getStepCardLabel = ({
  node,
  service,
}: {
  node: FlowNode,
  service?: Service,
}) => {
  if (hasUserDefinedStepTitle(node)) {
    return getStepTitle(node);
  }

  const serviceName = getServiceName(service);
  const editedTitle = getStepTitle(node);
  const defaultTitle = getDefaultStepTitle({ node, service });
  const baseLabel = editedTitle || defaultTitle;

  if (
    isZapierApp({ node, service }) ||
    !node.selected_api ||
    baseLabel === serviceName
  ) {
    return baseLabel;
  }
  return `${baseLabel} in ${serviceName}`;
};

export const isTriggerNode = (node: SkinnyNode) =>
  Boolean(node && isRead(node.type_of));

export const isEmptyNode = (node: FlowNode, services: ServicesMap) => {
  return (
    !isValidApp(node) || !isValidAction(node) || !isValidAuth(node, services)
  );
};

export const isEmptyActionNode = (node: FlowNode, services: ServicesMap) => {
  const isIncomplete =
    isEmptyNode(node, services) || isFilterWithEmptyCriteria(node);
  return !isTriggerNode(node) && isIncomplete;
};

export const isEmptyTriggerNode = (node: FlowNode, services: ServicesMap) => {
  return isTriggerNode(node) && isEmptyNode(node, services);
};

export const canTestFilter = ({
  node,
  shouldShowFilterTestResults,
}: {
  node: FlowNode,
  shouldShowFilterTestResults: boolean,
}) => !FieldUtils.filterCannotContinue(node) && !shouldShowFilterTestResults;

export const isFilterAPI = (selected_api: ?string) =>
  selected_api === 'FilterAPI';

export const getDefaultAction = (selected_api: ?string) =>
  isFilterAPI(selected_api) ? 'filter' : undefined;

export const getSubstepRoute = (selected_api: ?string) =>
  isFilterAPI(selected_api) ? 'filter' : undefined;

export const getCorrectNodeTypeOf = (selected_api: ?string, node: FlowNode) =>
  isFilterAPI(selected_api) ? 'filter' : node.type_of;

const NodeUtils = {
  EMPTY_ZAP_NAME,
  dependencies,
  service: getServiceAssociatedWithNode,
  isNodeTemplate,
  serviceNeedsAuth,
  sample,
  hasSampleData,
  hasNonSkipSearchResult,
  doAncestorsHaveFreshSampleData,
  sampleStatus,
  normalizedMeta,
  nodeAction,
  mergeNodeMetaInvalidMappings,
  isEqualNodeAttrs,
  hasHookURL,
  hookDirections,
  staticHookIdentifier,
  isStaticHook,
  isRestHook,
  isInstant,
  isHookPollable,
  isHookToPoll,
  canHookBeSilent,
  getNodeType,
  isHookSilent,
  mergeNodeAttrs,
  nodeParamsCurlies,
  inUseSelectedGivesMap,
  tempReplaceChoicesFromNode,
  hasInvalidMappings,
  shouldInvalidatedNeeds,
  isValidApp,
  isValidAuth,
  isValidAction,
  isValidFields,
  isValidSample,
  requiresLiveData,
  isValidSampleStatus,
  isValid,
  pollingInterval,
  updateNodeParams,
  updateSearchfillsMeta,
  newNodeMeta,
  hasShownInfo,
  hasShownAction,
  isConfigured,
  isSampleOutOfSync,
  hasSelectedApi,
  hasCodeApi,
  isActivatableNode,
  isAutomappedField,
  isFilterWithEmptyCriteria,
  isEmptyNode,
  isEmptyTriggerNode,
  isEmptyActionNode,
};

export default NodeUtils;
